"use client";
import React, { ReactNode, memo } from "react";
import { Box, BoxProps, InputLabelProps } from "@mui/material";
import {
  Control,
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  RegisterOptions,
} from "react-hook-form";
import { AppFromLabelField } from "@/common";
import { AppOptionType } from "@/models";
import AppVirtualizedCombobox, {
  AppVirtualizedComboboxProps,
} from "../AppVirtualizedCombobox";

const AppFormControlVirtualizedCombobox = <T extends FieldValues>({
  label,
  options,
  control,
  name,
  rules,
  isMultiple = false,
  controlProps,
  labelProps,
  actionsButton,
  autocompleteProps,
  onChangeValueForm,
  classNameContainer,
  childrenWrapperProps,
}: AppFormControlVirtualizedComboboxProps<T>) => {
  return (
    <AppFromLabelField
      label={label}
      labelProps={{
        ...labelProps,
      }}
      className={classNameContainer}
      required={labelProps?.required}
      childrenWrapperProps={childrenWrapperProps}
    >
      <Box className="flex items-center flex-row">
        <Controller
          control={control}
          name={name}
          rules={rules}
          render={({ field: { onChange, ...otherFieldProps } }) => {
            return (
              <AppVirtualizedCombobox
                {...otherFieldProps}
                disableCloseOnSelect={isMultiple}
                multiple={isMultiple}
                options={options}
                onChange={(_, data) => {
                  if (onChangeValueForm instanceof Function) {
                    onChangeValueForm(data);
                  }

                  onChange(data);
                }}
                {...autocompleteProps}
                textFieldProps={{
                  ...autocompleteProps?.textFieldProps,
                }}
              />
            );
          }}
          {...controlProps}
        />
        {actionsButton}
      </Box>
    </AppFromLabelField>
  );
};

export type AppFormControlVirtualizedComboboxProps<T extends FieldValues> = {
  label?: string | ReactNode;
  options: Array<AppOptionType>;
  control: Control<any>;
  name: FieldPath<T>;
  isMultiple?: boolean;
  rules?: RegisterOptions;
  controlProps?: Omit<ControllerProps, "render" | "name" | "control">;
  actionsButton?: ReactNode;
  labelProps?: InputLabelProps;
  autocompleteProps?: Omit<AppVirtualizedComboboxProps, "options">;
  onChangeValueForm?: (data?: Array<AppOptionType>) => void;
  classNameContainer?: string;
  childrenWrapperProps?: BoxProps;
};

export default memo(AppFormControlVirtualizedCombobox);
